// Sticky toolbar for the TipTap document editor.
// Sits above the page area in PagedEditor, provides formatting controls.

import { useState, useCallback, useRef, useEffect } from 'react';
import type { Editor } from '@tiptap/react';
import { Select } from '@/components/Select';
import { AnchoredMenu } from '@/components/AnchoredMenu';

// ---------------------------------------------------------------------------
// Color constants (same as FloatingToolbar)
// ---------------------------------------------------------------------------

const COLORS = [
  { label: 'Default', value: '' },
  { label: 'Red', value: '#ef4444' },
  { label: 'Orange', value: '#f97316' },
  { label: 'Yellow', value: '#eab308' },
  { label: 'Green', value: '#22c55e' },
  { label: 'Blue', value: '#3b82f6' },
  { label: 'Purple', value: '#a855f7' },
  { label: 'Pink', value: '#ec4899' },
];

// ---------------------------------------------------------------------------
// Toolbar button
// ---------------------------------------------------------------------------

function TBtn({
  onClick,
  isActive,
  disabled,
  title,
  children,
}: {
  onClick: () => void;
  isActive?: boolean;
  disabled?: boolean;
  title?: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-7 h-7 flex items-center justify-center rounded transition-colors shrink-0 ${
        disabled
          ? 'opacity-40 cursor-not-allowed'
          : isActive
            ? 'bg-accent text-accent-foreground'
            : 'text-foreground hover:bg-accent/50'
      }`}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Separator
// ---------------------------------------------------------------------------

function Sep() {
  return <div className="w-px h-5 bg-border mx-0.5 shrink-0" />;
}

// ---------------------------------------------------------------------------
// Color picker dropdown (reusable)
// ---------------------------------------------------------------------------

function ColorDropdown({
  editor,
  type,
  disabled,
  title,
}: {
  editor: Editor;
  type: 'text' | 'highlight';
  disabled?: boolean;
  title: string;
}) {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  const apply = useCallback(
    (color: string) => {
      if (type === 'text') {
        if (color) {
          editor.chain().focus().setColor(color).run();
        } else {
          editor.chain().focus().unsetColor().run();
        }
      } else {
        if (color) {
          editor.chain().focus().setHighlight({ color }).run();
        } else {
          editor.chain().focus().unsetHighlight().run();
        }
      }
      setOpen(false);
    },
    [editor, type],
  );

  return (
    <div className="relative" ref={triggerRef}>
      <TBtn
        onClick={() => setOpen(!open)}
        disabled={disabled}
        title={title}
      >
        {type === 'text' ? (
          <span className="text-sm font-bold" style={{ color: '#3b82f6' }}>A</span>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="#eab308" stroke="currentColor" strokeWidth="1">
            <rect x="3" y="3" width="18" height="18" rx="2" />
          </svg>
        )}
      </TBtn>
      <AnchoredMenu
        anchor={triggerRef}
        open={open}
        onClose={() => setOpen(false)}
        role="none"
        className="flex gap-1 p-1.5"
      >
          {COLORS.map((c) => (
            <button
              key={c.value || 'default'}
              type="button"
              onClick={() => apply(c.value)}
              title={c.label}
              className="w-5 h-5 rounded-sm border border-border/50 hover:scale-110 transition-transform"
              style={{
                backgroundColor: type === 'highlight'
                  ? (c.value || 'transparent')
                  : 'transparent',
                color: type === 'text' ? (c.value || 'currentColor') : undefined,
              }}
            >
              {type === 'text' && <span className="text-xs font-bold">A</span>}
            </button>
          ))}
      </AnchoredMenu>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Icons (inline SVGs, 16x16 / viewBox 24)
// ---------------------------------------------------------------------------

const icon = (d: string, fill = false) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill={fill ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d={d} />
  </svg>
);

const AlignLeftIcon = () => icon('M17 10H3 M21 6H3 M21 14H3 M17 18H3');
const AlignCenterIcon = () => icon('M21 6H3 M17 10H7 M21 14H3 M17 18H7');
const AlignRightIcon = () => icon('M21 10H7 M21 6H3 M21 14H3 M21 18H7');
const AlignJustifyIcon = () => icon('M21 10H3 M21 6H3 M21 14H3 M21 18H3');

const BulletListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="4" cy="6" r="1" fill="currentColor" /><circle cx="4" cy="12" r="1" fill="currentColor" /><circle cx="4" cy="18" r="1" fill="currentColor" />
  </svg>
);

const OrderedListIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="10" y1="6" x2="21" y2="6" /><line x1="10" y1="12" x2="21" y2="12" /><line x1="10" y1="18" x2="21" y2="18" />
    <text x="2" y="8" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">1</text>
    <text x="2" y="14" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">2</text>
    <text x="2" y="20" fontSize="7" fill="currentColor" stroke="none" fontFamily="sans-serif">3</text>
  </svg>
);

const ChecklistIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="4" width="4" height="4" rx="0.5" /><polyline points="4 6 5 7 7 4.5" />
    <line x1="10" y1="6" x2="21" y2="6" />
    <rect x="3" y="10" width="4" height="4" rx="0.5" />
    <line x1="10" y1="12" x2="21" y2="12" />
    <rect x="3" y="16" width="4" height="4" rx="0.5" />
    <line x1="10" y1="18" x2="21" y2="18" />
  </svg>
);

const LinkIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
    <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
  </svg>
);

const ImageIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <circle cx="8.5" cy="8.5" r="1.5" />
    <polyline points="21 15 16 10 5 21" />
  </svg>
);

const TableIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="3" y="3" width="18" height="18" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" /><line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="3" x2="9" y2="21" /><line x1="15" y1="3" x2="15" y2="21" />
  </svg>
);

const CodeBlockIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="16 18 22 12 16 6" /><polyline points="8 6 2 12 8 18" />
  </svg>
);

const BlockquoteIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" stroke="none">
    <path d="M6 17h3l2-4V7H5v6h3zm8 0h3l2-4V7h-6v6h3z" />
  </svg>
);

const HorizontalRuleIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
    <line x1="3" y1="12" x2="21" y2="12" />
  </svg>
);

const CalloutIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="8" x2="12" y2="12" />
    <line x1="12" y1="16" x2="12.01" y2="16" />
  </svg>
);

// ---------------------------------------------------------------------------
// Main EditorToolbar
// ---------------------------------------------------------------------------

interface EditorToolbarProps {
  editor: Editor | null;
}

export function EditorToolbar({ editor }: EditorToolbarProps) {
  // Force re-render on editor transactions so active states update
  const [, setTick] = useState(0);
  useEffect(() => {
    if (!editor) return;
    const handler = () => setTick((t) => t + 1);
    editor.on('transaction', handler);
    return () => { editor.off('transaction', handler); };
  }, [editor]);

  const disabled = !editor || !editor.isEditable;
  const e = editor; // shorthand

  // Helpers
  const run = useCallback(
    (cb: (chain: ReturnType<Editor['chain']>) => ReturnType<Editor['chain']>) => {
      if (!e) return;
      cb(e.chain().focus()).run();
    },
    [e],
  );

  // --- Text style dropdown value ---
  const currentStyle = e
    ? e.isActive('heading', { level: 1 })
      ? 'h1'
      : e.isActive('heading', { level: 2 })
        ? 'h2'
        : e.isActive('heading', { level: 3 })
          ? 'h3'
          : 'p'
    : 'p';

  const handleStyleChange = useCallback(
    (val: string) => {
      if (!e) return;
      switch (val) {
        case 'p':
          e.chain().focus().setParagraph().run();
          break;
        case 'h1':
          e.chain().focus().toggleHeading({ level: 1 }).run();
          break;
        case 'h2':
          e.chain().focus().toggleHeading({ level: 2 }).run();
          break;
        case 'h3':
          e.chain().focus().toggleHeading({ level: 3 }).run();
          break;
      }
    },
    [e],
  );

  // --- Link insert ---
  const handleLink = useCallback(() => {
    if (!e) return;
    const prev = e.getAttributes('link').href || '';
    const url = window.prompt('Enter URL:', prev);
    if (url === null) return; // cancelled
    if (url === '') {
      e.chain().focus().unsetLink().run();
    } else {
      e.chain().focus().setLink({ href: url }).run();
    }
  }, [e]);

  // --- Image insert ---
  const handleImage = useCallback(() => {
    if (!e) return;
    const url = window.prompt('Enter image URL:');
    if (!url) return;
    e.chain().focus().setImage({ src: url }).run();
  }, [e]);

  // --- Table insert ---
  const handleTable = useCallback(() => {
    if (!e) return;
    e.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [e]);

  // --- Callout insert ---
  const handleCallout = useCallback(() => {
    if (!e) return;
    // Insert via the callout node command if available, otherwise use insertContent
    if (typeof (e.commands as any).insertCallout === 'function') {
      (e.commands as any).insertCallout();
    } else {
      e.chain().focus().insertContent({ type: 'callout', attrs: { icon: '\uD83D\uDCA1' }, content: [{ type: 'text', text: ' ' }] }).run();
    }
  }, [e]);

  return (
    <div
      className="sticky top-0 z-30 flex items-center gap-0.5 px-2 py-1 border border-border bg-card rounded-lg mb-2 overflow-x-auto"
      role="toolbar"
      aria-label="Editor toolbar"
    >
      {/* 1. Text style dropdown */}
      <Select
        value={currentStyle}
        onChange={(ev) => handleStyleChange(ev.target.value)} disabled={disabled} className="mr-1 shrink-0" fullWidth={false} title="Text style">
        <option value="p">Paragraph</option>
        <option value="h1">Heading 1</option>
        <option value="h2">Heading 2</option>
        <option value="h3">Heading 3</option>
      </Select>

      {/* 2. Basic formatting */}
      <TBtn onClick={() => run((c) => c.toggleBold())} isActive={e?.isActive('bold')} disabled={disabled} title="Bold (Ctrl+B)">
        <span className="font-bold text-sm">B</span>
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleItalic())} isActive={e?.isActive('italic')} disabled={disabled} title="Italic (Ctrl+I)">
        <span className="italic text-sm">I</span>
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleUnderline())} isActive={e?.isActive('underline')} disabled={disabled} title="Underline (Ctrl+U)">
        <span className="underline text-sm">U</span>
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleStrike())} isActive={e?.isActive('strike')} disabled={disabled} title="Strikethrough">
        <span className="line-through text-sm">S</span>
      </TBtn>

      <Sep />

      {/* 4. Text alignment */}
      <TBtn onClick={() => run((c) => c.setTextAlign('left'))} isActive={e?.isActive({ textAlign: 'left' })} disabled={disabled} title="Align left">
        <AlignLeftIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.setTextAlign('center'))} isActive={e?.isActive({ textAlign: 'center' })} disabled={disabled} title="Align center">
        <AlignCenterIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.setTextAlign('right'))} isActive={e?.isActive({ textAlign: 'right' })} disabled={disabled} title="Align right">
        <AlignRightIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.setTextAlign('justify'))} isActive={e?.isActive({ textAlign: 'justify' })} disabled={disabled} title="Justify">
        <AlignJustifyIcon />
      </TBtn>

      <Sep />

      {/* 6. Lists */}
      <TBtn onClick={() => run((c) => c.toggleBulletList())} isActive={e?.isActive('bulletList')} disabled={disabled} title="Bullet list">
        <BulletListIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleOrderedList())} isActive={e?.isActive('orderedList')} disabled={disabled} title="Numbered list">
        <OrderedListIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleTaskList())} isActive={e?.isActive('taskList')} disabled={disabled} title="Checklist">
        <ChecklistIcon />
      </TBtn>

      <Sep />

      {/* 8. Insert blocks */}
      <TBtn onClick={handleLink} isActive={e?.isActive('link')} disabled={disabled} title="Insert link">
        <LinkIcon />
      </TBtn>
      <TBtn onClick={handleImage} disabled={disabled} title="Insert image">
        <ImageIcon />
      </TBtn>
      <TBtn onClick={handleTable} disabled={disabled} title="Insert table (3x3)">
        <TableIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleCodeBlock())} isActive={e?.isActive('codeBlock')} disabled={disabled} title="Code block">
        <CodeBlockIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.toggleBlockquote())} isActive={e?.isActive('blockquote')} disabled={disabled} title="Blockquote">
        <BlockquoteIcon />
      </TBtn>
      <TBtn onClick={() => run((c) => c.setHorizontalRule())} disabled={disabled} title="Horizontal rule">
        <HorizontalRuleIcon />
      </TBtn>
      <TBtn onClick={handleCallout} isActive={e?.isActive('callout')} disabled={disabled} title="Callout">
        <CalloutIcon />
      </TBtn>

      <Sep />

      {/* 10. Text color & highlight */}
      {e && <ColorDropdown editor={e} type="text" disabled={disabled} title="Text color" />}
      {e && <ColorDropdown editor={e} type="highlight" disabled={disabled} title="Highlight color" />}
    </div>
  );
}
